import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
// import { VectorSquare } from '@emotion-icons/fa-solid/VectorSquare'
import { Info } from '@emotion-icons/fa-solid/Info'
import { Colors } from 'src/components/theme'
import { Insight, InsightComponentProps } from 'src/components/insights/types'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { TradeAreaIcon } from 'src/components/insights/icons/trade-area'
import InsightLocked from 'src/components/insight-locked'
import { useTier } from 'src/hooks/use-tier'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'
import { getMMMYYYDate } from 'src/components/insights/custom-polygon-visits/utils'

import ttaPreview1 from 'src/assets/images/insights-previews/true-trade-area/tta_1.gif'
import ttaPreview1Placeholder from 'src/assets/images/insights-previews/true-trade-area/tta_1.png'
import ttaPreview2 from 'src/assets/images/insights-previews/true-trade-area/tta_2.gif'
import ttaPreview2Placeholder from 'src/assets/images/insights-previews/true-trade-area/tta_2.png'
import ttaPreview3 from 'src/assets/images/insights-previews/true-trade-area/tta_3.gif'
import ttaPreview3Placeholder from 'src/assets/images/insights-previews/true-trade-area/tta_3.png'
import { InsightContainer } from 'src/v2/views/owner-reveal/insights/container'
import { useRecoilValue } from 'recoil'
import * as atoms from 'src/atoms/custom-tta'
import * as searchAtoms from 'src/atoms/search'

const CustomPolygonTTAContent = dynamic(() => import('src/components/insights/custom-polygon-tta/content'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

const CustomPolygonVisitsPage: React.FC<InsightComponentProps> = (props) => {
  const { insight } = props
  const meetsTier = useTier(INSIGHTS_TIER['Custom Polygon True Trade Area'])
  const { start, end } = useRecoilValue(atoms.dateRange)
  const place = useRecoilValue(searchAtoms.googlePlace)

  const subtitle = useMemo(() => {
    if (!place) {
      return 'Custom Area'
    }
    const startDate = getMMMYYYDate(start)
    const endDate = getMMMYYYDate(end)
    return `${place.name} · ${place.formatted_address} · ${startDate} - ${endDate}`
  }, [place, start, end])

  return (
    <InsightContainer
      insight={insight}
      color={Colors.purpleDark}
      subtitle={subtitle}
      downloadFileName="Custom Polygon True Trade Area">
      <React.Suspense fallback={<FullScreenLoading />}>
        {!meetsTier ? <InsightLocked insight={insight} /> : <CustomPolygonTTAContent />}
      </React.Suspense>
    </InsightContainer>
  )
}

export const CustomPolygonTTA: Insight = {
  id: 'custom-polygon-tta',
  component: CustomPolygonVisitsPage,
  name: 'True Trade Area (Mobile Data)',
  // TODO: temporarily changed icons until we solve the negotiations with unacast
  // nameIcon: VectorSquare,
  // nameIconColor: Colors.bgGreenDark,
  nameIcon: Info,
  nameIconColor: Colors.neutral400,
  icon: TradeAreaIcon,
  supportedResultTypes: ['address', 'alphamap-location'],
  // supportedResultTypes: ['address', 'area', 'alphamap-location'],
  linkTooltipFile: 'insights-link-location-visits',
  tier: INSIGHTS_TIER['True Trade Area'],
  paywall: {
    title: 'See True Trade Area Reports in Action',
    description: `True Trade Area isn't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
    synopsis: [
      {
        image: ttaPreview1.src,
        imagePlaceholder: ttaPreview1Placeholder.src,
        title: 'Discover the Real Reach of Your Brand',
        description: 'Uncover the geographical spread of your customers with our in-depth trade area insights.',
      },
      {
        image: ttaPreview2.src,
        imagePlaceholder: ttaPreview2Placeholder.src,
        title: 'Strategize Expansion with Confidence',
        description:
          'Smartly navigate expansion and assess competitive landscapes, making informed decisions about where to grow.',
      },
      {
        image: ttaPreview3.src,
        imagePlaceholder: ttaPreview3Placeholder.src,
        title: 'Gain a Competitive Edge',
        description:
          'Quickly identify customer overlaps with competitors, leveraging TTA for strategic business positioning and smarter market moves.',
      },
    ],
  },
}
